import Button from "common/Buttons/Button.tsx";
import Flex from "common/Boxes/Flex.tsx";
import styled, { css, useTheme } from "styled-components";
import { Plus } from "lucide-react";
import {
  NEW_LOOK_ICON_STROKE,
  NEW_LOOK_SECTION_GAP,
  NEW_LOOK_PAGE_GUTTER_VAR,
} from "common/NewLook";
import TablePanel from "features/admin/NewLook/TablePanel";
import PanelTable from "features/admin/NewLook/PanelTable";
import useSearchInput from "utils/hooks/useSearchInput";
import InvoicesTable from "features/payouts/Invoices/InvoicesTable.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { LanguageNameSpaces, PayoutMethodSource } from "@shared/types";
import CreateInvoiceModal from "features/payouts/Invoices/CreateInvoiceModal.tsx";
import React from "react";
import PayoutMethods from "features/payouts/PayoutMethods/PayoutMethods.tsx";
import { SmartTableLayout } from "common/Layouts/SmartTableLayout.tsx";
import Card from "common/Boxes/Card.tsx";
import PayoutMethodsModal from "features/payouts/PayoutMethods/PayoutMethodsModal.tsx";
import { MOBILE_SEARCH } from "common/constants.ts";
import InvoicesCardsList from "features/payouts/Invoices/InvoicesCardsList.tsx";
import useInvoices from "features/payouts/Invoices/hooks/useInvoices.ts";

const Invoices = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PAYOUTS);
  const {
    isMobile,
    isSubUser,
    isSmallScreen,
    loading,
    clientsLoading,
    payoutMethodsFetching,
    invoices,
    invoicesFetching,
    refetch,
    paymentMethods,
    openCreateInvoiceModal,
    setOpenCreateInvoiceModal,
    openPayoutMethodsModal,
    setOpenPayoutMethodsModal,
    onCreateInvoiceClick,
  } = useInvoices();
  const { isNewLook } = useTheme();
  // New look (desktop): creating an invoice is the invoices panel's action
  // and the payout methods are a panel of their own (no page toolbar).
  const isNewLookIntro = isNewLook && !isMobile;
  const iconProps = { size: 16, strokeWidth: NEW_LOOK_ICON_STROKE };
  const [newLookSearch, newLookSearchValue] = useSearchInput({
    placeholder: t("invoices.search_placeholder"),
    style: { width: 240 },
  });

  const topContainer = isNewLook ? (
    !isSubUser ? (
      <NewLookMobileActions>
        {/* Text-only on phones so both labels fit. */}
        <Button onClick={() => setOpenPayoutMethodsModal(true)}>
          {t("payouts.manage_payout_methods")}
        </Button>
        <Button type="primary" onClick={onCreateInvoiceClick}>
          {t("invoices.buttons.create_invoice")}
        </Button>
      </NewLookMobileActions>
    ) : undefined
  ) : (
    <ButtonsWrapper>
      {!isSubUser && isSmallScreen && (
        <StyledButton
          type="primary"
          onClick={() => setOpenPayoutMethodsModal(true)}
        >
          {t("payouts.manage_payout_methods")}
        </StyledButton>
      )}
      {!isSubUser && (
        <StyledButton type="primary" onClick={onCreateInvoiceClick}>
          + {t("invoices.buttons.create_invoice")}
        </StyledButton>
      )}
    </ButtonsWrapper>
  );

  const cardsList = (
    <InvoicesCardsList
      data={invoices}
      loading={loading}
      refreshing={invoicesFetching}
      onRefresh={refetch}
      topContainer={topContainer}
    />
  );

  const invoicesTable = (
    <InvoicesTable
      invoices={invoices}
      loading={invoicesFetching || clientsLoading}
    />
  );

  // New look: the invoices as one flush panel (title, count, search and
  // the create action in its header); the payout methods panel sits beside
  // it on wide screens and under it otherwise.
  const tableLayout = isNewLookIntro ? (
    <NewLookWrapper>
      <ContentWrapper $isSmallScreen={isSmallScreen || isSubUser}>
        <TablePanel
          title={t("invoices.table.title")}
          count={invoices?.length || undefined}
          actions={
            <>
              {newLookSearch}
              {!isSubUser && (
                <Button type="primary" onClick={onCreateInvoiceClick}>
                  <Plus {...iconProps} />
                  {t("invoices.buttons.create_invoice")}
                </Button>
              )}
            </>
          }
          flush={true}
        >
          <PanelTable>
            <InvoicesTable
              invoices={invoices}
              loading={invoicesFetching || clientsLoading}
              searchFilter={newLookSearchValue}
            />
          </PanelTable>
        </TablePanel>
        {!isSubUser && (
          <PayoutMethods
            payoutMethods={paymentMethods}
            source={PayoutMethodSource.INVOICE}
            loading={payoutMethodsFetching}
            asPanel={true}
          />
        )}
      </ContentWrapper>
    </NewLookWrapper>
  ) : (
    <Wrapper>
      {topContainer}
      <ContentWrapper $isSmallScreen={isSmallScreen || isSubUser}>
        <Card style={{ overflow: "auto" }}>
          <SmartTableLayout
            tableComponent={invoicesTable}
            titleOrNode={t("invoices.table.title")}
            withSearch={true}
          />
        </Card>
        {!isSubUser && !isSmallScreen && (
          <PayoutMethods
            payoutMethods={paymentMethods}
            source={PayoutMethodSource.INVOICE}
            loading={payoutMethodsFetching}
          />
        )}
      </ContentWrapper>
    </Wrapper>
  );

  return (
    <div style={{ height: "100%" }}>
      {isMobile ? cardsList : tableLayout}
      {!isSubUser && (
        <>
          <CreateInvoiceModal
            open={openCreateInvoiceModal}
            closeModal={() => setOpenCreateInvoiceModal(false)}
            paymentMethods={paymentMethods}
          />
          <PayoutMethodsModal
            open={openPayoutMethodsModal}
            closeModal={() => setOpenPayoutMethodsModal(false)}
            payoutMethods={paymentMethods}
            source={PayoutMethodSource.INVOICE}
            loading={payoutMethodsFetching}
          />
        </>
      )}
    </div>
  );
};

export default Invoices;

const Wrapper = styled.div`
  padding: 16px;
  height: 100%;
  @media (max-width: ${MOBILE_SEARCH}) {
    padding: 0;
  }
`;
// New look: the invoices lead; the payout methods sit beside them on wide
// screens and under them otherwise.
const NewLookWrapper = styled.div`
  padding: 16px ${NEW_LOOK_PAGE_GUTTER_VAR} 32px;
  > div {
    gap: ${NEW_LOOK_SECTION_GAP}px;
    @media (max-width: 1500px) {
      display: flex;
      flex-direction: column;
      align-items: stretch;
    }
  }
`;
const NewLookMobileActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
  .ant-btn {
    flex: 1 1 auto;
    min-width: fit-content;
    height: 40px;
    padding: 0 12px;
    gap: 6px;
    font-size: 13px;
  }
  .ant-btn > span:last-child {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;
const ContentWrapper = styled.div<{ $isSmallScreen: boolean }>`
  display: grid;
  grid-template-columns: 1fr 350px;
  grid-gap: 16px;
  height: 100%;
  ${(p) =>
    p.$isSmallScreen &&
    `
    display: flex
  `};

  @media (max-width: 1400px) {
    grid-template-columns: 1fr 300px;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      height: auto;
      align-items: start;
      > * {
        min-width: 0;
        flex: 1;
      }
    `}
`;
const ButtonsWrapper = styled(Flex)`
  justify-content: flex-end;
  margin-bottom: 12px;
  column-gap: 4px;
  @media (max-width: ${MOBILE_SEARCH}) {
    padding-bottom: 16px;
    margin: 0;
  }
`;
const StyledButton = styled(Button)`
  @media (max-width: ${MOBILE_SEARCH}) {
    font-size: ${(p) => p.theme.typography.fontSizes.S};
    padding: 0 10px;
  }
`;
